// What the signup quiz leaves in the browser, and for whom. Kept free of the quiz's own components so the
// sign-out code can clear it without importing a page.
//
// Two stores, both in localStorage and both per user, because they hold different things:
//   * the answers to the optional steps, until Finish saves them. The quiz keeps its step in localStorage, so
//     the answers have to live there too: kept per tab, a closed tab brought the user back to steps marked done
//     with nothing in them, and Finish saved none of it. The email step's consent-screen round trip (a full page
//     load) is covered the same way. They go at Finish and when the session ends (see signoutWithDispatch.js).
//   * the Common Questions answers the quiz could not save: they are the only copy the user has, so they are
//     kept per user until the profile has saved them, whatever happens to the session.

const ANSWERS_PREFIX = "quiz_answers_";
const UNSAVED_PREFIX = "quiz_unsaved_answers_";

// Storage can be missing or throw (a browser that blocks it); the quiz still works without it.
const store = (kind) => {
  try {
    return kind === "session" ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
};

const readJson = (kind, key) => {
  try {
    return JSON.parse(store(kind)?.getItem(key) || "null");
  } catch {
    return null;
  }
};

const writeJson = (kind, key, value) => {
  try {
    store(kind)?.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the answers still live in the quiz's state, which is all this protects against.
  }
};

const remove = (kind, key) => {
  try {
    store(kind)?.removeItem(key);
  } catch {
    // Nothing to remove if storage is blocked.
  }
};

// ---- the optional steps' answers (this user, this browser) ---------------------------------------

// Builds before this one kept the answers in sessionStorage. A tab that was mid-quiz when this shipped still has
// them there; they are read from it once and written back to localStorage by the quiz's next save.
export const readQuizAnswers = (userId) => {
  if (!userId) return null;
  const key = `${ANSWERS_PREFIX}${userId}`;
  const saved = readJson("local", key) ?? readJson("session", key);
  return saved && typeof saved === "object" ? saved : null;
};

export const writeQuizAnswers = (userId, answers) => {
  if (userId) writeJson("local", `${ANSWERS_PREFIX}${userId}`, answers);
};

export const removeQuizAnswers = (userId) => {
  if (!userId) return;
  remove("local", `${ANSWERS_PREFIX}${userId}`);
  remove("session", `${ANSWERS_PREFIX}${userId}`);
};

const removePrefixed = (kind, prefix) => {
  const storage = store(kind);
  if (!storage) return;
  try {
    for (let i = storage.length - 1; i >= 0; i -= 1) {
      const key = storage.key(i);
      if (key && key.startsWith(prefix)) storage.removeItem(key);
    }
  } catch {
    // Nothing to clear if storage is blocked.
  }
};

/** Every user's saved quiz answers in this browser (and any older per-tab copy): for when the session ends, whoever it belonged to. */
export const removeAllQuizAnswers = () => {
  removePrefixed("local", ANSWERS_PREFIX);
  removePrefixed("session", ANSWERS_PREFIX);
};

// ---- the answers the quiz could not save (this user, this browser) --------------------------------

const isAnswer = (entry) => !!entry && typeof entry.classification === "string" && typeof entry.value === "string";

/** [{ classification, value }] still waiting to be saved for this user, or []. */
export const readUnsavedAnswers = (userId) => {
  const saved = userId ? readJson("local", `${UNSAVED_PREFIX}${userId}`) : null;
  return Array.isArray(saved) ? saved.filter(isAnswer) : [];
};

export const writeUnsavedAnswers = (userId, answers) => {
  if (!userId) return;
  if (answers.length) writeJson("local", `${UNSAVED_PREFIX}${userId}`, answers);
  else remove("local", `${UNSAVED_PREFIX}${userId}`);
};

export const removeUnsavedAnswers = (userId) => {
  if (userId) remove("local", `${UNSAVED_PREFIX}${userId}`);
};

/**
 * Tries again to save what the quiz could not. `save` is the Common Questions save. Resolves true once the
 * answers are saved and let go of, false when there were none; rejects, keeping them, when the save fails.
 */
export const retryUnsavedAnswers = async (userId, save) => {
  const pending = readUnsavedAnswers(userId);
  if (!pending.length) return false;
  await save(pending);
  removeUnsavedAnswers(userId);
  return true;
};
